const API_BASE = process.env.REACT_APP_API_URL || 'https://ourzestapp.online/oz_backend';

class ApiService {
    constructor() {
        this.token = null;
        this.refreshToken = null;
        this.csrfToken = null;
        this.userId = null;
        this.user = null;
        this.isRefreshing = false;
        this.refreshQueue = [];
        this.loadFromStorage();
    }

    loadFromStorage() {
        try {
            this.token = sessionStorage.getItem('auth_token') || null;
            this.refreshToken = sessionStorage.getItem('refresh_token') || null;
            this.csrfToken = localStorage.getItem('csrf_token') || null;
            this.userId = localStorage.getItem('user_id') || null;
            
            const userData = localStorage.getItem('user');
            if (userData) {
                try {
                    this.user = JSON.parse(userData);
                } catch (e) {
                    this.user = null;
                }
            }
            
            const expiry = localStorage.getItem('token_expiry');
            if (expiry && Date.now() > parseInt(expiry) * 1000) {
                this.clearAuth();
            }
        } catch (error) {
            console.error('Error loading auth data:', error);
        }
    }

    // ✅ GET AUTH STATUS
    getAuthStatus() {
        const isAuthenticated = !!(this.token && this.user);
        const tokenExpired = this.isTokenExpired();
        
        return {
            isAuthenticated: isAuthenticated && !tokenExpired,
            token: this.token ? {
                exists: true,
                expired: tokenExpired,
                expiry: this.getTokenExpiry()
            } : null,
            user: this.user ? {
                id: this.user.id,
                username: this.user.username,
                role: this.user.role
            } : null,
            hasRefreshToken: !!this.refreshToken,
            hasCsrfToken: !!this.csrfToken
        };
    }

    getTokenExpiry() {
        if (!this.token) return null;
        try {
            const payload = this.decodeToken(this.token);
            return payload?.exp ? new Date(payload.exp * 1000) : null;
        } catch {
            return null;
        }
    }

    setAuth(token, refreshToken) {
        this.token = token;
        this.refreshToken = refreshToken;
        sessionStorage.setItem('auth_token', token);
        if (refreshToken) sessionStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('auth_timestamp', Date.now().toString());
        
        const payload = this.decodeToken(token);
        if (payload && payload.exp) {
            localStorage.setItem('token_expiry', payload.exp.toString());
        }
        if (payload && payload.user_id) {
            localStorage.setItem('user_id', payload.user_id);
        }
    }

    setUser(user) {
        this.user = user;
        localStorage.setItem('user', JSON.stringify(user));
        if (user && user.role) {
            localStorage.setItem('user_role', user.role);
        }
    }

    clearAuth() {
        this.token = null;
        this.refreshToken = null;
        this.csrfToken = null;
        this.userId = null;
        this.user = null;
        
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('refresh_token');
        localStorage.removeItem('auth_timestamp');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('user_id');
        localStorage.removeItem('csrf_token');
        localStorage.removeItem('user');
        localStorage.removeItem('user_role');
        localStorage.removeItem('auth_token_exists');
        localStorage.removeItem('refresh_token_exists');
        
        this.refreshQueue = [];
        this.isRefreshing = false;
    }

    decodeToken(token) {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            return JSON.parse(atob(parts[1]));
        } catch {
            return null;
        }
    }

    isTokenExpired() {
        if (!this.token) return true;
        try {
            const payload = this.decodeToken(this.token);
            if (!payload || !payload.exp) return true;
            return Date.now() > (payload.exp * 1000 - 5 * 60 * 1000);
        } catch {
            return true;
        }
    }

    async refreshAuthToken() {
        try {
            if (!this.refreshToken) return false;
            
            const response = await fetch(`${API_BASE}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: this.refreshToken }),
                credentials: 'include'
            });
            
            if (!response.ok) return false;
            
            const data = await response.json();
            if (data.token) {
                this.setAuth(data.token, data.refresh_token);
                if (data.csrf_token) this.csrfToken = data.csrf_token;
                return true;
            }
            return false;
        } catch (error) {
            console.error('Token refresh error:', error);
            return false;
        }
    }

    async request(endpoint, options = {}) {
        const method = options.method || 'GET';
        const body = options.body ? JSON.stringify(options.body) : '';
        const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        
        if (this.token && this.isTokenExpired() && !options._skipTokenCheck) {
            const refreshed = await this.refreshAuthToken();
            if (!refreshed) {
                this.clearAuth();
                if (typeof window !== 'undefined') window.location.href = '/login';
                throw new Error('Session expired');
            }
        }
        
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...options.headers
        };
        
        if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
        if (this.csrfToken && method !== 'GET') headers['X-CSRF-Token'] = this.csrfToken;
        
        const url = `${API_BASE}${path}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        try {
            const response = await fetch(url, {
                method,
                headers,
                body: body || undefined,
                credentials: 'include',
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (response.status === 401) {
                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.refreshQueue.push({ resolve, reject, url, options, headers });
                    });
                }
                
                this.isRefreshing = true;
                try {
                    const refreshed = await this.refreshAuthToken();
                    if (refreshed) {
                        headers['Authorization'] = `Bearer ${this.token}`;
                        const retryResponse = await fetch(url, {
                            method,
                            headers,
                            body: body || undefined,
                            credentials: 'include',
                            signal: controller.signal
                        });
                        this.processQueue();
                        return this.handleResponse(retryResponse);
                    }
                } finally {
                    this.isRefreshing = false;
                }
                
                this.clearAuth();
                if (typeof window !== 'undefined') window.location.href = '/login';
                throw new Error('Session expired');
            }
            
            return this.handleResponse(response);
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') throw new Error('Request timeout');
            throw error;
        }
    }

    processQueue() {
        this.refreshQueue.forEach(({ resolve, reject, url, options, headers }) => {
            headers['Authorization'] = `Bearer ${this.token}`;
            fetch(url, { ...options, headers, credentials: 'include' })
                .then(this.handleResponse.bind(this))
                .then(resolve)
                .catch(reject);
        });
        this.refreshQueue = [];
    }

    async handleResponse(response) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const text = await response.text();
            console.error('Non-JSON response:', text);
            throw new Error('Server returned non-JSON response');
        }
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Request failed');
        if (data.csrf_token) this.csrfToken = data.csrf_token;
        return data;
    }

    async login(username, password) {
        try {
            const response = await fetch(`${API_BASE}/index.php?module=auth&action=login`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ username, password }),
                credentials: 'include'
            });
            
            if (!response.ok) {
                const text = await response.text();
                console.error('Login error response:', text);
                throw new Error(`Server error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.token) {
                this.setAuth(data.token, data.refresh_token);
                if (data.csrf_token) this.csrfToken = data.csrf_token;
                if (data.user) {
                    this.setUser(data.user);
                }
                return data;
            }
            
            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    async validateToken() {
        try {
            const response = await this.request('/auth/validate', { 
                method: 'GET',
                _skipTokenCheck: true 
            });
            if (response.status === 'success' && response.user) {
                this.setUser(response.user);
                return { success: true, user: response.user };
            }
            return { success: false };
        } catch (error) {
            console.error('Token validation error:', error);
            return { success: false, error: error.message };
        }
    }

    async logout() {
        try {
            await this.request('/auth/logout', { method: 'POST', _skipTokenCheck: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAuth();
        }
    }

    // ============================================
    // PRODUCT METHODS
    // ============================================
    async getProducts(module = 'cosmetics', params = {}) {
        return this.request(`/${module}/get_records`, {
            method: 'POST',
            body: { action: 'get_records', ...params }
        });
    }

    async getProduct(module, id, params = {}) {
        return this.request(`/${module}/get_record`, {
            method: 'POST',
            body: { action: 'get_record', id, ...params }
        });
    }

    async addProduct(module, productData) {
        return this.request(`/${module}/add`, {
            method: 'POST',
            body: { action: 'add', ...productData }
        });
    }

    async updateProduct(module, id, productData) {
        return this.request(`/${module}/edit`, {
            method: 'POST',
            body: { action: 'edit', id, ...productData }
        });
    }

    async deleteProduct(module, id) {
        return this.request(`/${module}/delete`, {
            method: 'POST',
            body: { action: 'delete', id }
        });
    }

    async searchProducts(module, searchTerm, params = {}) {
        return this.request(`/${module}/search_products`, {
            method: 'POST',
            body: { action: 'search_products', searchTerm, ...params }
        });
    }

    // ============================================
    // SHOP METHODS
    // ============================================
    async getShops() {
        return this.request('/shop/get_shops', {
            method: 'POST',
            body: { action: 'get_shops' }
        });
    }

    async switchShop(shopId) {
        return this.request('/shop/switch', {
            method: 'POST',
            body: { action: 'switch', shop_id: shopId }
        });
    }

    async getCurrentShop() {
        return this.request('/shop/current', {
            method: 'POST',
            body: { action: 'current' }
        });
    }

    // ============================================
    // USER METHODS
    // ============================================
    async getUserProfile() {
        return this.request('/user/profile', {
            method: 'GET'
        });
    }

    async updateUserProfile(data) {
        return this.request('/user/profile', {
            method: 'PUT',
            body: data
        });
    }

    async changePassword(currentPassword, newPassword) {
        return this.request('/user/change-password', {
            method: 'POST',
            body: { current_password: currentPassword, new_password: newPassword }
        });
    }

    // ============================================
    // UTILITY METHODS
    // ============================================
    async healthCheck() {
        try {
            const response = await fetch(`${API_BASE}/health`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            return response.ok;
        } catch {
            return false;
        }
    }
}

export default new ApiService();